import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBotReply, getWelcomeMessage } from './chatbotKnowledge';

const Chatbot = ({ handleBookNow }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([{ ...getWelcomeMessage(), sender: "bot" }]);
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);
  const lastMessageRef = useRef(null);
  const inputRef = useRef(null);
  const contextRef = useRef({});
  const navigate = useNavigate();

  useEffect(() => {
    // Open long answers at their first line, not their last
    const showAnswerStart = !isTyping && messages[messages.length - 1].sender === "bot";

    if (showAnswerStart) {
      lastMessageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping, isOpen]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const send = (text) => {
    if (!text.trim() || isTyping) return;

    setMessages(prev => [...prev, { text, sender: "user" }]);
    setInput("");
    setIsTyping(true);

    const { context, ...reply } = getBotReply(text, contextRef.current);
    contextRef.current = context;

    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, { ...reply, sender: "bot" }]);
    }, 500);
  };

  const runAction = (action) => {
    setIsOpen(false);
    if (action.type === "book") handleBookNow();
    if (action.type === "accommodation") navigate("/accommodation");
  };

  const actionStyle = "block w-full text-center px-3 py-2 rounded-xl text-xs font-bold transition";
  const lastMessage = messages[messages.length - 1];

  return (
    <div className="fixed bottom-24 right-6 z-[9999] font-sans flex flex-col items-end">
      {isOpen && (
        <div className="bg-white/95 backdrop-blur-md shadow-[0_20px_60px_rgba(0,0,0,0.2)] rounded-[2rem] w-[calc(100vw-3rem)] max-w-[370px] h-[min(560px,calc(100vh-13rem))] flex flex-col mb-4 border border-white/20 overflow-hidden">

          {/* Header */}
          <div className="bg-gradient-to-br from-[#1e40af] to-[#2563eb] px-6 py-4 text-white shadow-lg">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <h3 className="text-xl font-black tracking-tight uppercase">V2V Support</h3>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  <p className="text-[10px] font-bold text-blue-100 tracking-widest uppercase">Automated Assistant</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} aria-label="Close chat" className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Chat Body */}
          <div className="flex-1 p-4 overflow-y-auto bg-slate-50 space-y-4" aria-live="polite">
            {messages.map((msg, idx) => (
              <div key={idx} ref={idx === messages.length - 1 ? lastMessageRef : null} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[88%] space-y-2">
                  <div className={`px-4 py-3 shadow-sm text-[13px] leading-relaxed whitespace-pre-wrap break-words ${
                    msg.sender === 'user'
                      ? 'bg-[#1e40af] text-white rounded-2xl rounded-tr-none'
                      : 'bg-white text-gray-700 border border-gray-200 rounded-2xl rounded-tl-none'
                  }`}>
                    {msg.text}
                  </div>

                  {msg.actions?.map((action) =>
                    action.href ? (
                      <a
                        key={action.label}
                        href={action.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${actionStyle} bg-green-600 text-white hover:bg-green-700`}
                      >
                        {action.label}
                      </a>
                    ) : (
                      <button
                        key={action.label}
                        type="button"
                        onClick={() => runAction(action)}
                        className={`${actionStyle} bg-[#1e40af] text-white hover:bg-blue-900`}
                      >
                        {action.label}
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex gap-1 items-center p-2 bg-gray-200 w-12 rounded-full justify-center ml-2">
                <span className="w-1 h-1 bg-gray-500 rounded-full animate-bounce"></span>
                <span className="w-1 h-1 bg-gray-500 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                <span className="w-1 h-1 bg-gray-500 rounded-full animate-bounce [animation-delay:0.4s]"></span>
              </div>
            )}

            {/* Quick questions */}
            {!isTyping && lastMessage.suggestions && (
              <div className="flex flex-wrap gap-2">
                {lastMessage.suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => send(suggestion)}
                    className="px-3 py-1.5 rounded-full border border-blue-200 bg-white text-xs font-semibold text-[#1e40af] hover:bg-blue-50 transition"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-3 bg-white border-t border-gray-100">
            <div className="flex items-center gap-3 bg-gray-100 rounded-2xl px-4 py-3 shadow-inner focus-within:ring-2 ring-blue-500/20 transition-all">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send(input)}
                placeholder="Ask V2V anything..."
                aria-label="Ask V2V a question"
                className="flex-1 min-w-0 bg-transparent border-none text-sm outline-none text-gray-800 placeholder:text-gray-400"
              />
              <button onClick={() => send(input)} aria-label="Send message" className={`p-1 transition-all ${input.trim() ? 'text-blue-700 scale-125' : 'text-gray-300'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 rotate-90" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Launcher */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? "Close chat" : "Open chat"}
        className="bg-[#1e40af] text-white w-14 h-14 rounded-2xl shadow-[0_15px_40px_rgba(30,64,175,0.4)] flex items-center justify-center text-2xl hover:scale-110 active:scale-95 transition-all duration-300 relative"
      >
        {isOpen ? "✕" : "💬"}
        {!isOpen && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-blue-500"></span>
          </span>
        )}
      </button>
    </div>
  );
};

export default Chatbot;
